import { useEffect, useRef, useState } from 'react';

type AsyncResult<T> =
  | {
      data: T;
      loading: false;
    }
  | {
      data: T | undefined;
      loading: true;
    };

/**
 * Allows use of async getter functions in a react function component, similar to how common "useQuery" hooks work.
 * Assumes the component will deal with the missing data while it is loading, and does not reload if the function
 * changes. To reload on dependency change, pass a dependency array
 *
 * Example usage
 *
 * ```tsx
 *   const {data: item, loading} = useAsync(
       async () => {
         const resp = await getItem(itemId)
         return resp.data
       },
      [itemId]
    );
 * ```
 */
const useAsync = <T>(
  func: () => Promise<T>,
  dependencies: unknown[] = []
): AsyncResult<T> => {
  const funcRef = useRef(func);
  funcRef.current = func;

  const [data, setData] = useState<T | undefined>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setLoading(true);
    void funcRef.current().then(d => {
      setData(d);
      setLoading(false);
    });
  }, dependencies);

  return {
    data,
    loading,
  } as AsyncResult<T>;
};

export default useAsync;
